// src/admin/screens/kyc/KycQueueScreen.tsx
// The landing page for the KYC reviewer role. Filter chips bucket the queue:
// "Pending review" combines pending + manual_review (both need a human).
// Filter is URL-bound so a deep link or reload preserves position.
//
// Row click navigates to /kyc/:id (the review screen).
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Filter } from 'lucide-react';
import DataTable, { type Column } from '@admin/components/DataTable';
import FilterChip from '@admin/components/FilterChip';
import StatusChip from '@admin/components/StatusChip';
import SectionHead from '@admin/components/SectionHead';
import Button from '@admin/components/Button';
import Avatar from '@admin/components/Avatar';
import { useKycQueue, type KycQueueRow } from '@admin/hooks/useKycQueue';
import { KYC_STATUS_LABEL, KYC_STATUS_TONE } from '@admin/utils/kycStatus';
import { formatCatDateTime } from '@admin/utils/dates';
import { formatZambiaPhone } from '@admin/utils/phone';
import type { Database } from '@/lib/database.types';
import styles from './KycQueueScreen.module.css';

type KycStatus = Database['public']['Enums']['kyc_status'];

interface Bucket {
  key: string;
  label: string;
  values: KycStatus[];
}
const BUCKETS: Bucket[] = [
  { key: 'pending', label: 'Pending review', values: ['pending', 'manual_review'] },
  { key: 'verified', label: 'Verified', values: ['verified'] },
  { key: 'rejected', label: 'Rejected', values: ['rejected'] },
  { key: 'all', label: 'All', values: ['pending', 'manual_review', 'verified', 'rejected'] },
];

function findBucket(key: string | null): Bucket {
  return BUCKETS.find((b) => b.key === key) ?? BUCKETS[0]!;
}

export default function KycQueueScreen() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const bucket = findBucket(params.get('bucket'));
  const { data, isLoading, error } = useKycQueue({ status: bucket.values });
  const rowCount = data?.length ?? 0;

  const description = bucket.key === 'pending'
    ? `${rowCount} submission${rowCount === 1 ? '' : 's'} awaiting review`
    : `${rowCount} ${bucket.label.toLowerCase()} submission${rowCount === 1 ? '' : 's'}`;

  const cols = useMemo<Column<KycQueueRow>[]>(
    () => [
      {
        key: 'borrower',
        header: 'Applicant',
        render: (r) => {
          const name = r.profiles?.display_name ?? r.profiles?.full_name ?? '—';
          return (
            <div className={styles.applicantCell}>
              <Avatar name={name} size={28} />
              <span className={styles.applicantName}>{name}</span>
            </div>
          );
        },
      },
      { key: 'phone', header: 'Phone', render: (r) => formatZambiaPhone(r.profiles?.phone) },
      {
        key: 'nrc',
        header: 'NRC',
        render: (r) => (
          <span className="mono" style={{ fontSize: 12.5 }}>{r.nrc_number ?? '—'}</span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => (
          <StatusChip label={KYC_STATUS_LABEL[r.status]} tone={KYC_STATUS_TONE[r.status]} />
        ),
      },
      {
        key: 'submitted',
        header: 'Submitted',
        render: (r) => (
          <span style={{ color: 'var(--c-text-muted)', fontSize: 12.5 }}>
            {formatCatDateTime(r.submitted_at)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className={styles.page}>
      <SectionHead
        eyebrow="Compliance"
        title="KYC review queue"
        description={description}
        action={<Button variant="secondary" size="sm" leftIcon={<Filter size={14} />}>Filters</Button>}
      />

      <div className={styles.chipsRow}>
        {BUCKETS.map((b) => (
          <FilterChip
            key={b.key}
            label={b.label}
            active={bucket.key === b.key}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (b.key === 'pending') next.delete('bucket');
              else next.set('bucket', b.key);
              setParams(next, { replace: true });
            }}
          />
        ))}
      </div>

      <DataTable
        rows={data}
        columns={cols}
        isLoading={isLoading}
        error={(error as Error | null)?.message ?? null}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/kyc/${r.id}`)}
        emptyLabel="Nothing matches the current filter."
      />
    </div>
  );
}
